<template>
  <div class="cfca-agreement-sign-dialog">
    <el-dialog
      :visible="visible"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
      custom-class="cfca-agreement-el-dialog"
      width="880px"
      @close="handleClose"
    >
      <div class="cfca-title">{{ $t('cfcaAgreement.title') }}</div>

      <div ref="contentRef" class="cfca-content">
        <preview-pdf
          v-if="agreementPdfUrl"
          ref="previewPdf"
          :url="agreementPdfUrl"
          class="cfca-pdf-preview"
          @scroll="handleScroll"
          @loaded="handlePdfLoaded"
        />
        <div v-else-if="pdfLoading" class="cfca-pdf-loading">
          <i class="el-icon-loading"></i>
        </div>
      </div>

      <div class="cfca-check-row">
        <el-checkbox
          v-model="checked"
          :disabled="!canCheckBox"
          class="cfca-checkbox"
        >
          <span class="cfca-check-text">
            {{ $t('cfcaAgreement.checkPrefix') }}
            <a
              class="cfca-link"
              :href="agreementLink"
              target="_blank"
              rel="noopener noreferrer"
              @click.stop
            >
              {{ $t('cfcaAgreement.linkText') }}
            </a>
          </span>
        </el-checkbox>
      </div>

      <div class="cfca-subtips">
        <div class="cfca-subtips-line">
          {{ $t('cfcaAgreement.tipScrollToBottom') }}
        </div>
      </div>

      <div class="cfca-actions">
        <lls-button class="cfca-btn cfca-btn-cancel" @click="handleCancel">
          {{ $t('cfcaAgreement.cancel') }}
        </lls-button>
        <lls-button
          type="primary"
          class="cfca-btn cfca-btn-confirm"
          :disabled="!checked"
          @click="handleConfirm"
        >
          {{ $t('cfcaAgreement.confirmSigning') }}
        </lls-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import PreviewPdf from '@/components/previewPdf.vue'

export default {
  name: 'CfcaAgreementSignDialog',
  components: {
    PreviewPdf,
  },
  props: {
    cfcaAggrementId: {
      type: String,
      default: '',
    },
    /** 协议模板 PDF 的请求路径（与 queryCfcaAgreement 返回的 cfcaTemplatePath 一致） */
    cfcaTemplatePath: {
      type: String,
      default: '',
    },
    visible: {
      type: Boolean,
      default: false,
    },
    agreementLink: {
      type: String,
      default: 'https://www.cfca.com.cn/zhengshuzizhu/cfcaszzsfwxy/',
    },
    minReadSeconds: {
      type: Number,
      default: 5,
    },
  },
  data() {
    return {
      agreementPdfUrl: '',
      pdfLoading: false,
      checked: false,
      readSeconds: 0,
      hasScrolledToBottom: false,
      timer: null,
      /** 弹窗打开时刻，北京时间 YYYY-MM-DD HH:mm:ss */
      dialogShowTime: '',
      fallbackDeviceId: '',
    }
  },
  computed: {
    canCheckBox() {
      return this.hasScrolledToBottom
    },
    canConfirm() {
      return (
        this.checked &&
        this.hasScrolledToBottom &&
        this.readSeconds >= this.minReadSeconds
      )
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        if (val) this.resetAndStart()
        else {
          this.stopTimer()
          this.revokeAgreementPdfUrl()
        }
      },
    },
  },
  beforeDestroy() {
    this.stopTimer()
    this.revokeAgreementPdfUrl()
  },
  methods: {
    revokeAgreementPdfUrl() {
      if (this.agreementPdfUrl) {
        URL.revokeObjectURL(this.agreementPdfUrl)
        this.agreementPdfUrl = ''
      }
    },
    async loadAgreementPdf() {
      const path = (this.cfcaTemplatePath || '').trim()
      this.revokeAgreementPdfUrl()
      if (!path) return
      this.pdfLoading = true
      try {
        const res = await this.$http.get(path, {
          handingResponse: true,
          responseType: 'blob',
        })
        const blob = res.data
        if (blob instanceof Blob) {
          this.agreementPdfUrl = URL.createObjectURL(blob)
        }
      } catch (e) {
        console.log(e)
      } finally {
        this.pdfLoading = false
      }
    },
    getScrollContainer() {
      const pdf = this.$refs.previewPdf
      if (!pdf || !pdf.$el) return null
      return pdf.$el.querySelector('.home_wrap')
    },
    checkScrollPosition() {
      const el = this.getScrollContainer()
      if (!el) return
      const threshold = 2
      if (el.scrollHeight <= el.clientHeight + threshold) {
        this.hasScrolledToBottom = true
        return
      }
      const atBottom =
        el.scrollTop + el.clientHeight >= el.scrollHeight - threshold
      if (atBottom) this.hasScrolledToBottom = true
    },
    handlePdfLoaded() {
      this.$nextTick(() => this.checkScrollPosition())
    },
    /** 北京时间 YYYY-MM-DD HH:mm:ss（与浏览器本地时区无关） */
    formatBeijingDateTime(date = new Date()) {
      const f = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Shanghai',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      })
      const parts = f.formatToParts(date)
      const v = (t) => (parts.find((x) => x.type === t) || {}).value || '00'
      return `${v('year')}-${v('month')}-${v('day')} ${v('hour')}:${v('minute')}:${v('second')}`
    },
    generateBrowserDeviceId() {
      return `web_${Date.now().toString(36)}_${Math.random()
        .toString(36)
        .slice(2, 15)}`
    },
    /** 浏览器侧稳定设备标识（localStorage 持久化） */
    getBrowserDeviceId() {
      const storageKey = 'kj_browser_device_id'
      try {
        let id = localStorage.getItem(storageKey)
        if (!id) {
          id = this.generateBrowserDeviceId()
          localStorage.setItem(storageKey, id)
        }
        return id
      } catch (e) {
        if (!this.fallbackDeviceId) {
          this.fallbackDeviceId = this.generateBrowserDeviceId()
        }
        return this.fallbackDeviceId
      }
    },
    async resetAndStart() {
      this.dialogShowTime = this.formatBeijingDateTime(new Date())
      this.checked = false
      this.readSeconds = 0
      this.hasScrolledToBottom = false
      await this.loadAgreementPdf()
      this.stopTimer()
      this.timer = setInterval(() => {
        this.readSeconds += 1
      }, 1000)
      this.$nextTick(() => {
        const el = this.getScrollContainer()
        if (el) el.scrollTop = 0
      })
    },
    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    handleScroll() {
      this.checkScrollPosition()
    },
    emitVisible(val) {
      this.$emit('update:visible', val)
    },
    handleClose() {
      this.emitVisible(false)
      this.$emit('close')
    },
    handleCancel() {
      this.emitVisible(false)
      this.$emit('cancel')
    },
    async handleConfirm() {
      if (!this.hasScrolledToBottom) {
        this.$message({
          type: 'warning',
          showClose: true,
          message: this.$t('cfcaAgreement.msgScrollToBottom'),
        })
        return
      }
      if (this.readSeconds < this.minReadSeconds) {
        this.$message({
          type: 'warning',
          showClose: true,
          message: this.$t('cfcaAgreement.tipReadSeconds', {
            sec: this.minReadSeconds - this.readSeconds,
          }),
        })
        return
      }
      if (!this.checked) {
        this.$message({
          type: 'warning',
          showClose: true,
          message: this.$t('cfcaAgreement.msgPleaseCheck'),
        })
        return
      }

      if (!this.canConfirm) return
      try {
        await this.$API.system.signCfcaAgreement({
          contractId: this.cfcaAggrementId,
          showTime: this.dialogShowTime,
          deviceId: this.getBrowserDeviceId(),
        })
        this.$emit('confirm')
        this.emitVisible(false)
      } catch (error) {
        console.log(error)
      }
    },
  },
}
</script>

<style scoped>
.cfca-title {
  text-align: center;
  font-size: 18px;
  color: #2f70d6;
  font-weight: 500;
  margin: 4px 0 10px;
}

.cfca-content {
  height: 360px;
  overflow: hidden;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
}

.cfca-pdf-preview {
  width: 100%;
  height: 100%;
  margin: 0;
}

.cfca-content ::v-deep .preview-wrapper {
  width: 100%;
  height: 100%;
  margin: 0;
}

.cfca-content ::v-deep .preview-item-wrapper {
  width: 100%;
  height: 100%;
}

.cfca-content ::v-deep .home_wrap {
  width: 100%;
  height: 100%;
}

.cfca-pdf-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #909399;
  font-size: 24px;
}

.cfca-check-row {
  margin-top: 14px;
  display: flex;
  justify-content: flex-start;
}

.cfca-check-text {
  font-size: 13px;
  color: #333;
}

.cfca-link {
  color: #2f70d6;
  text-decoration: underline;
}

.cfca-subtips {
  margin-top: 6px;
  color: #909399;
  font-size: 12px;
  line-height: 18px;
}

.cfca-actions {
  margin-top: 14px;
  display: flex;
  justify-content: center;
  gap: 18px;
}
</style>

<style>
.cfca-agreement-el-dialog {
  border-radius: 6px;
}
.cfca-agreement-el-dialog .el-dialog__header {
  padding: 10px 10px 0;
}
.cfca-agreement-el-dialog .el-dialog__headerbtn {
  top: 10px;
}
.cfca-agreement-el-dialog .el-dialog__body {
  padding: 8px 22px 18px;
}
</style>
